import { useAuth } from "hooks/Auth";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import LoadingIndicator from "components/General/LoadingIndicator";
import { transform as contentTransformer } from "schemas/Content";
import { transform as collectionTransformer } from "schemas/Collection";
import { apiGet } from "helpers/Request";
import { getPaginationMeta } from "schemas/Util";
import ContentStackedList from "./ContentStackedList";
import ContentGridList from "./ContentGridList";
import ButtonGroup from "components/Buttons/ButtonGroup";
import Pagination from "components/General/Pagination";
import { toast } from "react-hot-toast";
import { modal } from "components/HeadlessModal";
import PrimaryButton from "components/Buttons/PrimaryButton";
import { useSelector } from "react-redux";

const ContentPicker = (props) => {
    const [state, setState] = useState([]);

    const bubbleSelectAction = (item) => {
        toast.success("Content added!");
        if (props.allowMultiple) {
            setState(state.push(item));
            return;
        }
        if (props.onSelect) {
            props.onSelect.call(this, item);
        }

        if (props.allowMultiple === false) {
            modal.dismiss();
        }
    };

    // props.allowMultiple = true;
    // props.onSelect

    const overlayProps = {
        title: "Select Content",
        variant: "wide",
        children: (
            <ContentPickerContent {...props} onSelect={bubbleSelectAction} />
        ),
        onCancel: () => {
            if (props.onRequestClose) {
                props.onRequestClose();
            }
        },
        buttons: () => (
            <PrimaryButton
                onClick={() => {
                    if (props.allowMultiple) {
                        props.onSelect(state);
                    }
                    if (props.onRequestClose) {
                        props.onRequestClose();
                    }
                    modal.dismiss();
                }}
            >
                Add items
            </PrimaryButton>
        ),
    };

    useEffect(() => {
        setState([]);
        if (props.open) {
            modal(overlayProps);
        } else {
            modal.dismiss();
        }
        // TODO: not sure the best way to handle this dependency loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    return null;
};

const ContentPickerContent = (props) => {
    const auth = useAuth();
    const settings = useSelector((state) => state.contentFlow?.settings);

    const defaultParams = {
        per_page: 20,
        type: props.type ? props.type : null,
        mediatype: props.mediaType ? props.mediaType : null,
        keywords: null,
        page: 1,
        list_type: props.listType ? props.listType : "list",
        mode: settings.settings?.ffContentCache ? "meili" : "default",
    };

    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState([]);
    const [collections, setCollections] = useState([]);
    const [queryParams, setQueryParams] = useState(defaultParams);
    const [paginationMeta, setPaginationMeta] = useState({});

    const updateQueryParams = (params) => {
        const newParams = Object.assign({}, defaultParams, queryParams, params);
        setQueryParams(newParams);
    };

    const loadContent = useCallback(() => {
        setLoading(true);
        apiGet(
            "/v4/content/",
            auth.publication,
            Object.assign({}, defaultParams, queryParams)
        ).then((resp) => {
            setContent(contentTransformer(resp));
            setPaginationMeta(getPaginationMeta(resp));
            setLoading(false);
        });
        // eslint-disable-next-line
    }, [queryParams, auth.publication]);

    const handleKeywordChange = (e) => {
        if (e.key === "Enter") {
            if (e.target.value) {
                updateQueryParams({ keywords: e.target.value });
                // loadContent();
            } else {
                updateQueryParams({ keywords: null });
                // loadContent();
            }
        }
    };

    const loadCollections = useCallback(() => {
        apiGet("/v4/collection", auth.publication, {
            sort: "name",
            order: "asc",
        }).then((resp) => {
            setCollections(collectionTransformer(resp));
        });
    }, [auth.publication]);

    useEffect(() => {
        loadContent();
        loadCollections();
    }, [queryParams, loadContent, loadCollections]);

    return (
        <Fragment>
            <input
                type="text"
                className="block w-full rounded-md border-gray-300 py-2 px-4 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                placeholder="Search"
                onKeyUp={handleKeywordChange}
            />
            <div className="my-4 flex flex-wrap justify-center gap-2">
                <div>
                    <ButtonGroup
                        variant="xs"
                        buttons={[
                            <button
                                type="button"
                                onClick={() => updateQueryParams(null)}
                            >
                                Reset
                            </button>,
                        ]}
                    />
                </div>
                <div>
                    <ButtonGroup
                        variant="xs"
                        buttons={[
                            <button
                                type="button"
                                selected={queryParams.list_type === "list"}
                                onClick={updateQueryParams.bind(null, {
                                    list_type: "list",
                                })}
                            >
                                List
                            </button>,
                            <button
                                type="button"
                                selected={queryParams.list_type === "grid"}
                                onClick={updateQueryParams.bind(null, {
                                    list_type: "grid",
                                })}
                            >
                                Grid
                            </button>,
                        ]}
                    />
                </div>
                <div>
                    <ButtonGroup
                        variant="xs"
                        buttons={[
                            <button
                                type="menu"
                                buttons={collections.map((c, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            updateQueryParams({
                                                collection: c.id,
                                            });
                                        }}
                                    >
                                        {c.name}
                                    </button>
                                ))}
                            >
                                {queryParams.collection
                                    ? collections.find(
                                          (c) =>
                                              c.id ===
                                              parseInt(queryParams.collection)
                                      )?.name
                                    : "Collection"}
                            </button>,
                        ]}
                    />
                </div>
                <div className="md:grid md:flex-grow md:justify-items-end">
                    <ButtonGroup
                        variant="xs"
                        buttons={[
                            <button
                                type="button"
                                selected={
                                    !queryParams.type && !queryParams.mediatype
                                }
                                onClick={updateQueryParams.bind(null, {
                                    type: null,
                                    mediatype: null,
                                })}
                            >
                                Any
                            </button>,
                            <button
                                type="button"
                                selected={queryParams.type === "article"}
                                onClick={updateQueryParams.bind(null, {
                                    type: "article",
                                    mediatype: null,
                                })}
                            >
                                Article
                            </button>,
                            <button
                                type="button"
                                selected={queryParams.type === "page"}
                                onClick={updateQueryParams.bind(null, {
                                    type: "page",
                                    mediatype: null,
                                })}
                            >
                                Page
                            </button>,
                            <button
                                type="button"
                                selected={queryParams.mediatype === "image"}
                                onClick={updateQueryParams.bind(null, {
                                    type: "media",
                                    mediatype: "image",
                                })}
                            >
                                Image
                            </button>,
                            <button
                                type="button"
                                selected={queryParams.mediatype === "video"}
                                onClick={updateQueryParams.bind(null, {
                                    type: "media",
                                    mediatype: "video",
                                })}
                            >
                                Video
                            </button>,
                            <button
                                type="button"
                                selected={queryParams.mediatype === "gallery"}
                                onClick={updateQueryParams.bind(null, {
                                    type: "media",
                                    mediatype: "gallery",
                                })}
                            >
                                Gallery
                            </button>,
                            <button
                                type="button"
                                selected={queryParams.mediatype === "audio"}
                                onClick={updateQueryParams.bind(null, {
                                    type: "media",
                                    mediatype: "audio",
                                })}
                            >
                                Audio
                            </button>,

                            <button
                                type="button"
                                selected={queryParams.mediatype === "pdf"}
                                onClick={updateQueryParams.bind(null, {
                                    type: "media",
                                    mediatype: "pdf",
                                })}
                            >
                                PDF
                            </button>,
                        ]}
                    />
                </div>
            </div>
            <div className="relative z-0 mb-4">
                <Pagination
                    variant="xs"
                    onClick={(meta) => {
                        updateQueryParams({ page: meta.page });
                    }}
                    {...paginationMeta}
                />
            </div>
            <div
                className="relative z-0 overflow-auto border-t border-gray-100 pt-2"
                style={{ maxHeight: "60vh" }}
            >
                {loading ? (
                    <LoadingIndicator />
                ) : (
                    <Fragment>
                        {queryParams.list_type === "list" ? (
                            <ContentStackedList
                                items={content}
                                onSelect={props.onSelect}
                            />
                        ) : (
                            <ContentGridList
                                items={content}
                                onSelect={props.onSelect}
                            />
                        )}
                    </Fragment>
                )}
            </div>
        </Fragment>
    );
};

export default ContentPicker;
